/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaFacebook, FaLinkedin, FaInstagram, FaTwitter } from "react-icons/fa";
import axios from "axios";

const Footer = () => {
  // State to store API data
  const [settings, setSettings] = useState(null);
  const [footerLogo, setFooterLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Function to handle scrolling to top on navigation
  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // API configuration
  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/settings/ui`;
  const API_MEDIA = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;

  // Function to fetch image from media API
  const fetchFooterLogo = async (mediaId) => {
    if (!mediaId) return null;
    
    try {
      // Get the actual media ID (could be object or string)
      const id = typeof mediaId === 'object' ? mediaId.id || mediaId._id : mediaId;
      if (!id) return null;
      
      const response = await axios.get(`${API_MEDIA}/${id}/download`, {
        responseType: "blob",
      });
      
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error("Error fetching footer logo:", error);
      return null;
    }
  };

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // console.log("🔍 Fetching data from API...");
        
        const response = await axios.get(API_URL);
        const apiData = response.data;
        
        // console.log("✅ API Response:", apiData);
        
        // Find the active setting
        let setting = null;
        
        if (apiData.results && Array.isArray(apiData.results)) {
          setting = apiData.results[0] || apiData.results.find(s => s.isActive);
        } else if (Array.isArray(apiData)) {
          setting = apiData[0] || apiData.find(s => s.isActive);
        } else if (typeof apiData === 'object') {
          setting = apiData;
        }
        
        if (setting) {
          // console.log("🎯 Setting found:", setting);
          setSettings(setting);
          
          // Fetch footer logo if available
          if (setting.footerLogoMediaId) {
            const logoUrl = await fetchFooterLogo(setting.footerLogoMediaId);
            setFooterLogo(logoUrl);
          }
        } else {
          // console.log("⚠️ No setting found in API response");
        }
      } catch (error) {
        console.error("❌ Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Cleanup function for blob URL
    return () => {
      if (footerLogo) {
        URL.revokeObjectURL(footerLogo);
      }
    };
  }, []);

  // Show loading state
  if (loading) {
    return (
      <footer className="bg-gradient-to-r from-gray-900 to-black text-white py-8 px-6 shadow-lg">
        <div className="container mx-auto text-center py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-800 rounded w-1/4 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-800 rounded w-3/4 mx-auto mb-6"></div>
            <div className="h-4 bg-gray-800 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-gradient-to-r from-gray-900 to-black text-white py-8 px-6 shadow-lg">
      <div className="container mx-auto grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Logo & Description - DYNAMIC */}
        <div className="space-y-3">
          {/* Dynamic Website Name */}
          <h2 className="text-2xl font-extrabold tracking-tight">
            {settings?.websiteName}
          </h2>
          
          {/* Dynamic Description */}
          <p className="text-gray-400 text-sm">
            {settings?.description}
            <hr/>
          </p>
          
          <div className="flex items-center space-x-2">
            {/* Dynamic Footer Logo */}
            {footerLogo ? (
              <img 
                src={footerLogo}
                alt={`${settings?.websiteName} Footer Logo`}
                className="h-12 w-12 object-contain"
                onError={(e) => {
                  console.error("Failed to load footer logo");
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="h-12 w-12 bg-gray-800 rounded flex items-center justify-center">
                <span className="text-gray-400 text-xs">Logo</span>
              </div>
            )}
            
            {/* Dynamic Invitation Text */}
            <p className="text-gray-400 text-xl">
              An initiative by{" "}
              {settings?.invitationByLink ? (
                <a
                  href={settings.invitationByLink}
                  className="text-teal-400 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {settings?.invitationByName}
                </a>
              ) : (
                <span className="text-teal-400">
                  {settings?.invitationByName}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Quick Links - Two Columns (Still static) */}
        <div>
          <h3 className="text-lg font-semibold border-b border-gray-700 pb-1 mb-3">Quick Links</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <Link 
              to="/about" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300"
              onClick={handleLinkClick}
            >
              About Us
            </Link>
            <Link 
              to="/events" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300"
              onClick={handleLinkClick}
            >
              Events
            </Link>
            <Link 
              to="/membership" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300"
              onClick={handleLinkClick}
            >
              Membership
            </Link>
            <Link 
              to="/ContactUs" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300"
              onClick={handleLinkClick}
            >
              Contact Us
            </Link>
            <a
              href="/MASAAwards"
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 col-span-2 md:col-span-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              Masa Awards
            </a>
            <a
              href="https://www.mantrasworld.com/"
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 col-span-2 md:col-span-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              Mantra's World
            </a>
            <a
              href="https://mantrasfoundation.org/"
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300 col-span-2 md:col-span-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              Mantras Foundation
            </a>
            <Link 
              to="/TermsAndConditions" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300"
              onClick={handleLinkClick}
            >
              Terms & Conditions
            </Link>
            <Link 
              to="/PrivacyPolicy" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300"
              onClick={handleLinkClick}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/CancellationAndRefund" 
              className="text-gray-400 hover:text-teal-400 transition-colors duration-300"
              onClick={handleLinkClick}
            >
              CancellationAndRefund
            </Link>
          </div>
        </div>

        {/* Contact Information - NOW DYNAMIC */}
        <div>
          <h3 className="text-lg font-semibold border-b border-gray-700 pb-1 mb-3">Contact Us</h3>
          
          {/* Dynamic Email */}
          {settings?.contactUs?.email ? (
            <p className="text-gray-400 text-sm">
              Email:{" "}
              <a 
                href={`mailto:${settings.contactUs.email}`}
                className="hover:text-teal-400 transition-colors duration-300"
              >
                {settings.contactUs.email}
              </a>
            </p>
          ) : (
            <p className="text-gray-400 text-sm">
              Email:{" "}
              <span className="text-gray-500">Not provided</span>
            </p>
          )}
          
          {/* Dynamic Phone */}
          {settings?.contactUs?.phone ? (
            <p className="text-gray-400 text-sm mt-1">
              Phone:{" "}
              <a 
                href={`tel:${settings.contactUs.phone.replace(/\D/g, '')}`}
                className="hover:text-teal-400 transition-colors duration-300"
              >
                {settings.contactUs.phone}
              </a>
            </p>
          ) : (
            <p className="text-gray-400 text-sm mt-1">
              Phone:{" "}
              <span className="text-gray-500">Not provided</span>
            </p>
          )}
        </div>

        {/* Social Media Links - NOW DYNAMIC */}
        <div>
          <h3 className="text-lg font-semibold border-b border-gray-700 pb-1 mb-3">Follow Us</h3>
          <div className="flex space-x-4 justify-center md:justify-start">
            
            {/* Facebook - Dynamic */}
            {settings?.socialMedia?.facebook && (
              <a 
                href={settings.socialMedia.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-teal-400 transition-colors duration-300"
                title="Follow us on Facebook"
              >
                <FaFacebook size={22} />
              </a>
            )}
            
            {/* LinkedIn - Dynamic */}
            {settings?.socialMedia?.linkedin && (
              <a 
                href={settings.socialMedia.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-teal-400 transition-colors duration-300"
                title="Follow us on LinkedIn"
              >
                <FaLinkedin size={22} />
              </a>
            )}
            
            {/* Instagram - Dynamic */}
            {settings?.socialMedia?.instagram && (
              <a 
                href={settings.socialMedia.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-teal-400 transition-colors duration-300"
                title="Follow us on Instagram"
              >
                <FaInstagram size={22} />
              </a>
            )}
            
            {/* Twitter - Dynamic */}
            {settings?.socialMedia?.twitter && (
              <a 
                href={settings.socialMedia.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-teal-400 transition-colors duration-300"
                title="Follow us on Twitter/X"
              >
                <FaTwitter size={22} />
              </a>
            )}
            
            {/* Show message if no social media links */}
            {(!settings?.socialMedia?.facebook && 
              !settings?.socialMedia?.linkedin && 
              !settings?.socialMedia?.instagram && 
              !settings?.socialMedia?.twitter) && (
              <p className="text-gray-500 text-sm">
                No social media links available
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Copyright - NOW DYNAMIC */}
      <div className="border-t border-gray-800 mt-6 pt-4 text-center text-gray-500 text-xs">
        {settings?.footerCopyrightContent ? (
          settings.footerCopyrightContent
        ) : (
          `© ${new Date().getFullYear()} ${settings?.websiteName || "MASA Forum"}. All rights reserved.`
        )}
      </div>
    </footer>
  );
};

export default Footer;